import React from 'react';
import { motion } from 'framer-motion';
const CYCLE_DURATION = 4;
export const TrackingIllustration: React.FC = () => {
  return <div className="relative w-full h-full bg-sky-soft overflow-hidden" data-id="element-606">
      {/* Background grid pattern */}
      <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none" data-id="element-607">
        <defs data-id="element-608">
          <pattern id="track-grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-609">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#2d8fa8" strokeWidth="0.3" data-id="element-610" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#track-grid)" data-id="element-611" />
      </svg>

      {/* Overhead Scanner */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 flex flex-col items-center" data-id="element-612">
        <div className="bg-primary rounded-md px-3 py-2 shadow-glossy flex items-center gap-2" data-id="element-613">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-leaf" animate={{
          opacity: [0.4, 1, 0.4]
        }} transition={{
          duration: 1,
          repeat: Infinity
        }} data-id="element-614" />
          <span className="font-mono text-[9px] text-white tracking-wider" data-id="element-615">
            SCANNER
          </span>
        </div>
        {/* Scanner head pointing down */}
        <div className="w-12 h-3 bg-primary rounded-b-lg shadow-glossy" data-id="element-616" />
        <div className="w-8 h-1 bg-sky" data-id="element-617" />
      </div>

      {/* Scan beam — pulses when bottle is under scanner */}
      <motion.div className="absolute top-12 left-1/2 -translate-x-1/2 w-16 origin-top pointer-events-none" style={{
      height: '90px'
    }} animate={{
      opacity: [0, 0, 0.7, 0.7, 0, 0]
    }} transition={{
      duration: CYCLE_DURATION,
      repeat: Infinity,
      times: [0, 0.25, 0.35, 0.55, 0.65, 1],
      ease: 'linear'
    }} data-id="element-618">
        <svg viewBox="0 0 64 90" className="w-full h-full" preserveAspectRatio="none" data-id="element-619">
          <defs data-id="element-620">
            <linearGradient id="beam-grad" x1="0" y1="0" x2="0" y2="1" data-id="element-621">
              <stop offset="0%" stopColor="#3aa856" stopOpacity="0.5" data-id="element-622" />
              <stop offset="100%" stopColor="#3aa856" stopOpacity="0" data-id="element-623" />
            </linearGradient>
          </defs>
          <polygon points="20,0 44,0 64,90 0,90" fill="url(#beam-grad)" data-id="element-624" />
          {/* Scan lines */}
          <motion.line x1="0" x2="64" y1="20" y2="20" stroke="#3aa856" strokeWidth="0.8" opacity="0.8" animate={{
          y1: [20, 80, 20],
          y2: [20, 80, 20]
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-625" />
        </svg>
      </motion.div>

      {/* Animated scan rings around bottle when scanned */}
      <motion.div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{
      top: '110px'
    }} animate={{
      opacity: [0, 0, 1, 1, 0, 0]
    }} transition={{
      duration: CYCLE_DURATION,
      repeat: Infinity,
      times: [0, 0.28, 0.32, 0.55, 0.6, 1]
    }} data-id="element-626">
        {[0, 1, 2].map(i => <motion.div key={i} className="absolute left-0 top-0 border-2 border-leaf rounded-full" style={{
        translateX: '-50%',
        translateY: '-50%'
      }} animate={{
        width: [20, 110],
        height: [20, 110],
        opacity: [0.9, 0]
      }} transition={{
        duration: 1.4,
        repeat: Infinity,
        delay: i * 0.45,
        ease: 'easeOut'
      }} data-id="element-627" />)}
      </motion.div>

      {/* Bottle ID readout — appears when scanning */}
      <motion.div className="absolute top-16 right-6 bg-white rounded-lg shadow-glossy px-3 py-2 border border-leaf/30" animate={{
      opacity: [0, 0, 1, 1, 0, 0],
      x: [10, 10, 0, 0, 0, 0]
    }} transition={{
      duration: CYCLE_DURATION,
      repeat: Infinity,
      times: [0, 0.28, 0.35, 0.55, 0.6, 1]
    }} data-id="element-628">
        <div className="flex items-center gap-1.5 mb-1" data-id="element-629">
          <span className="w-1.5 h-1.5 rounded-full bg-leaf animate-pulse" data-id="element-630" />
          <span className="font-mono text-[8px] text-leaf uppercase tracking-wider" data-id="element-631">
            Detected
          </span>
        </div>
        <div className="font-mono text-[10px] text-primary font-medium" data-id="element-632">
          CYB-8492-A
        </div>
        <div className="font-mono text-[8px] text-muted" data-id="element-633">CYCLE 14 OK</div>
      </motion.div>

      {/* The traveling bottle */}
      <motion.div className="absolute" style={{
      top: '108px'
    }} animate={{
      left: ['-10%', '46%', '46%', '78%', '78%'],
      opacity: [1, 1, 1, 1, 0]
    }} transition={{
      duration: CYCLE_DURATION,
      repeat: Infinity,
      times: [0, 0.25, 0.6, 0.8, 0.85],
      ease: 'linear'
    }} data-id="element-634">
        {/* Drop-in animation when reaching crate */}
        <motion.div animate={{
        y: [0, 0, 0, 0, 30],
        rotate: [0, 0, 0, 0, 15]
      }} transition={{
        duration: CYCLE_DURATION,
        repeat: Infinity,
        times: [0, 0.25, 0.6, 0.8, 0.85],
        ease: 'easeIn'
      }} data-id="element-635">
          <BottleWithTag data-id="element-636" />
        </motion.div>
      </motion.div>

      {/* "+1" feedback that pops when bottle is added */}
      <motion.div className="absolute font-mono font-medium text-leaf text-sm pointer-events-none" style={{
      right: '60px',
      bottom: '70px'
    }} animate={{
      opacity: [0, 0, 0, 0, 1, 0],
      y: [0, 0, 0, 0, 0, -30]
    }} transition={{
      duration: CYCLE_DURATION,
      repeat: Infinity,
      times: [0, 0.25, 0.6, 0.82, 0.86, 1]
    }} data-id="element-637">
        +1
      </motion.div>

      {/* Conveyor belt */}
      <div className="absolute bottom-12 left-0 right-32 h-3 bg-ink overflow-hidden" data-id="element-638">
        <motion.div className="flex h-full" animate={{
        x: ['0%', '-10%']
      }} transition={{
        duration: 0.8,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-639">
          {Array.from({
          length: 40
        }).map((_, i) => <div key={i} className="shrink-0 w-3 h-full border-r border-muted/40" data-id="element-640" />)}
        </motion.div>
      </div>
      <div className="absolute bottom-10 left-2 w-2 h-2 rounded-full bg-muted" data-id="element-641" />
      <div className="absolute bottom-10 right-32 w-2 h-2 rounded-full bg-muted" data-id="element-642" />

      {/* Crate (receiving bottles) */}
      <div className="absolute bottom-6 right-6 w-28" data-id="element-643">
        {/* Crate body */}
        <div className="relative" data-id="element-644">
          <svg viewBox="0 0 120 80" className="w-full h-auto" data-id="element-645">
            {/* Crate slots — bottles visible inside */}
            <rect x="2" y="20" width="116" height="56" fill="#c97a4a" stroke="#0f3d2e" strokeWidth="1.5" rx="2" data-id="element-646" />
            <rect x="2" y="20" width="116" height="8" fill="#0f3d2e" opacity="0.3" data-id="element-647" />

            {/* Existing bottles in the crate (3 rows of 4) */}
            {[{
            x: 10,
            c: '#3aa856'
          }, {
            x: 36,
            c: '#1a3a2e'
          }, {
            x: 62,
            c: '#3aa856'
          }, {
            x: 88,
            c: '#e0a73d'
          }].map((b, i) => <g key={i} data-id="element-648">
                <rect x={b.x} y="28" width="8" height="40" fill={b.c} rx="1" data-id="element-649" />
                <rect x={b.x + 2} y="24" width="4" height="6" fill={b.c} data-id="element-650" />
                <rect x={b.x + 2} y="22" width="4" height="3" fill="#0f3d2e" data-id="element-651" />
                <rect x={b.x + 1.5} y="50" width="5" height="6" fill="rgba(255,255,255,0.2)" data-id="element-652" />
              </g>)}

            {/* Crate label */}
            <rect x="40" y="58" width="40" height="14" fill="#faf8f3" stroke="#0f3d2e" strokeWidth="0.5" rx="1" data-id="element-653" />
            <text x="60" y="64" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="3" fill="#0f3d2e" data-id="element-654">
              CRATE
            </text>
            <text x="60" y="68.5" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="3" fill="#3aa856" data-id="element-655">
              #A-204
            </text>
          </svg>
        </div>

        {/* Count badge */}
        <motion.div className="absolute -top-3 -right-3 bg-leaf text-white rounded-full w-12 h-12 flex flex-col items-center justify-center shadow-leaf-glow" animate={{
        scale: [1, 1, 1, 1.15, 1]
      }} transition={{
        duration: CYCLE_DURATION,
        repeat: Infinity,
        times: [0, 0.6, 0.82, 0.86, 0.95]
      }} data-id="element-656">
          <span className="font-mono text-[8px] leading-none opacity-80" data-id="element-657">
            FILLED
          </span>
          <span className="font-mono text-sm font-medium leading-none mt-0.5" data-id="element-658">
            12/24
          </span>
        </motion.div>
      </div>

      {/* Status bar at top-left */}
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-md px-2.5 py-1.5 shadow-glossy" data-id="element-659">
        <div className="flex items-center gap-1.5" data-id="element-660">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-sky" animate={{
          opacity: [0.4, 1, 0.4]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} data-id="element-661" />
          <span className="font-mono text-[8px] text-primary uppercase tracking-wider" data-id="element-662">
            Live Tracking
          </span>
        </div>
      </div>
    </div>;
};
const BottleWithTag: React.FC = () => <div className="relative w-10 h-20" data-id="element-663">
    <svg viewBox="0 0 40 80" className="w-full h-full" data-id="element-664">
      {/* Cap */}
      <rect x="14" y="2" width="12" height="8" rx="1" fill="#e0a73d" data-id="element-665" />
      {/* Neck */}
      <rect x="15" y="10" width="10" height="12" fill="#3aa856" data-id="element-666" />
      {/* Body */}
      <path d="M 15 22 Q 8 26 8 36 L 8 72 Q 8 76 12 76 L 28 76 Q 32 76 32 72 L 32 36 Q 32 26 25 22 Z" fill="#3aa856" data-id="element-667" />
      {/* Shine */}
      <path d="M 11 30 Q 10 50 11 70" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" fill="none" data-id="element-668" />
      {/* Label */}
      <rect x="11" y="42" width="18" height="16" fill="rgba(255,255,255,0.18)" data-id="element-669" />
      {/* Smart tag — prominent on the base */}
      <rect x="13" y="62" width="14" height="9" rx="1" fill="#2d8fa8" stroke="#fff" strokeWidth="0.5" data-id="element-670" />
      <circle cx="20" cy="66.5" r="1.5" fill="#fff" data-id="element-671" />
      <path d="M 17 64.5 Q 20 62.5 23 64.5" stroke="#fff" strokeWidth="0.4" fill="none" data-id="element-672" />
      <path d="M 16 66.5 Q 20 63.5 24 66.5" stroke="#fff" strokeWidth="0.4" fill="none" data-id="element-673" />
    </svg>

    {/* Tag callout */}
    <div className="absolute -right-1 top-12 font-mono text-[7px] text-sky bg-white/90 px-1 rounded shadow-sm whitespace-nowrap" data-id="element-674">
      TAG
    </div>
  </div>;